// src/utils/brandHelper.ts

import { useTranslation } from 'react-i18next';
import { Branch } from './supabaseClient';
import { ArTranslation } from '../types/custom';

/**
 * Get the localized name for a branch/restaurant based on current language
 * @param branch - The branch object
 * @param language - Current language ('en' or 'ar')
 * @returns Localized restaurant name
 */
export const getBranchName = (branch: Branch, language: string): string => {
	const arTranslation = branch.ar as ArTranslation | null;
	if (language === 'ar' && arTranslation?.name) {
		return arTranslation.name;
	}
	return branch.name || '';
};

/**
 * Get the localized description for a branch/restaurant based on current language
 * @param branch - The branch object
 * @param language - Current language ('en' or 'ar')
 * @returns Localized restaurant description
 */
export const getBranchDescription = (branch: Branch, language: string): string | null => {
	const arTranslation = branch.ar as ArTranslation | null;
	if (language === 'ar' && arTranslation?.description) {
		return arTranslation.description;
	}
	return branch.description;
};

/**
 * Get the localized address for a branch/restaurant based on current language
 * @param branch - The branch object
 * @param language - Current language ('en' or 'ar')
 * @returns Localized restaurant address
 */
export const getBranchAddress = (branch: Branch, language: string): string | null => {
	const arTranslation = branch.ar as ArTranslation | null;
	if (language === 'ar' && arTranslation?.address) {
		// Handle both string and array formats
		if (Array.isArray(arTranslation.address)) {
			return arTranslation.address.join('\n');
		}
		return arTranslation.address;
	}
	// Handle both string and array formats for default address
	if (Array.isArray(branch.address)) {
		return branch.address.join('\n');
	}
	return branch.address;
};
/**
 * Hook to get all localized branch data
 * @param branch - The branch object
 * @returns Object with localized name, description, and address
 */
export const useLocalizedBranch = (branch: Branch | null) => {
	const { i18n } = useTranslation();

	if (!branch) {
		return {
			name: '',
			description: null,
			address: null
		};
	}

	return {
		name: getBranchName(branch, i18n.language),
		description: getBranchDescription(branch, i18n.language),
		address: getBranchAddress(branch, i18n.language)
	};
};

/**
 * Get localized branch data without using hooks (for use in non-component contexts)
 * @param branch - The branch object
 * @param language - Current language ('en' or 'ar')
 * @returns Object with localized name, description, and address
 */
export const getLocalizedBranchData = (branch: Branch | null, language: string) => {
	if (!branch) {
		return {
			name: '',
			description: null,
			address: null
		};
	}

	return {
		name: getBranchName(branch, language),
		description: getBranchDescription(branch, language),
		address: getBranchAddress(branch, language)
	};
};